import { BehaviorSubject, interval, from, of } from 'rxjs';
import { switchMap, startWith, tap, catchError, share, map, filter } from 'rxjs/operators';
import { getCookie } from '@utiljs/cookie';

import request from '@utils/axiosWrapper';
import * as Ids from '@utils/ids';
import { getStorage, setStorage } from '@utils/localStorage';
import { ifPoiEnableAutoAcceptLocally } from '@utils/poiHelper';
import { getGrayStateByToken, GRAY_AB_TOKEN, getDowngradeByBit } from '@utils/grayAB';
import { CUSTOM_METRICS, reportCustomMetrics } from '@utils/owlReporter';

import networkMonitor from '@shell/utils/monitorNetworkStatus';
import { RawOrderBaseInfo } from '@business/pages/OrderManage/shared/types';
import {
    OrderDetailInfo,
    AllCountInfo,
    OrderTagCode,
} from '@business/pages/OrderManage/wmUnifiedOrder/types';
import { OrderBaseInfo as PhfOrderBaseInfo } from '@business/pages/OrderManage/phfOrder/types';
import { RootMasterMessageHub } from '../../master';
import { OrderBusinessType, OrderType } from '../shared/types';
import CustomSourceKey from './key';

/** 餐饮新形态灰度 */
const UNIFIED_ORDER_GRAY = getGrayStateByToken(GRAY_AB_TOKEN.UNIFIED_ORDER);

const lastValueKey = 'messageHub.newOrderDetailPoller.lastNormalMessage';
export const getLastNormalMessage = (): NewOrderDetailMessage | null => {
    const json = getStorage(lastValueKey);
    if (!json) return null;
    try {
        // FIXME: should add validation
        return JSON.parse(json);
    } catch {
        return null;
    }
};
export const setLastNormalMessage = (value: NewOrderDetailMessage) => {
    setStorage(lastValueKey, JSON.stringify(value));
};

interface DataChannel extends AllCountInfo {
    /** 判断自动接单的方式 - APP用 */
    selfService: number;
    /** 退款详情 */
    refundDetails: {
        /** 订单序号 */
        num: number;
        /** 订单viewid */
        orderViewId: number;
    }[];
    /** 催单详情 */
    reminderDetails: {
        /** 订单序号 */
        num: number;
        /** 订单viewid */
        orderViewId: number;
    }[];
    /** 是否展示预订单 Tab 右上角小红点 */
    showPreOrderRedPoint: boolean;
}

export interface IndexOrderInfo {
    businessType: OrderBusinessType;
    /** 外卖/拼好饭订单的公共信息（仅包含必要的订单信息） */
    commonInfo: CommonInfo;
}

export interface CommonInfo {
    wmOrderViewId: string;
    wmPoiId: number;
    /** 订单类型 */
    orderType: OrderType;
    /** 订单标签 */
    orderTag: OrderTagCode;
}

type RawPostNewOrderListResponse = {
    code: number;
    msg: string;
    data: {
        newOrders: RawOrderBaseInfo[];
        newIndexOrders?: IndexOrderInfo[];
        dataChannel: DataChannel;
        /** 下一个时间戳 */
        dateAndSeq: string;
        /** count的下一个时间戳 */
        countDateAndSeq: string;
        signal: number;
        /** 新订单轮询间隔（秒） */
        intervalSec: number;
        /** 服务器时间戳 */
        serverTime: number;
        /** 灰度标识（降级用） */
        signalGray: number;
    };
};

export type NewOrderDetailMessage = Omit<
    RawPostNewOrderListResponse['data'],
    'newOrders' | 'newIndexOrders'
> & {
    newOrders: (OrderDetailInfo | PhfOrderBaseInfo)[];
    newIndexOrders?: IndexOrderInfo[];
};

export { PhfOrderBaseInfo };

export const isIndexOrderVo = (
    order: OrderDetailInfo | PhfOrderBaseInfo | IndexOrderInfo,
): order is IndexOrderInfo => {
    return 'commonInfo' in order;
};

/**
 * 仅对轮询接口的数据结构适用，并非业务层面（即 businessType）的判断
 *
 * TODO：是不是可以直接用父层级里的 businessType 判断？
 */
export const isPhfOrderVo = (
    order: OrderDetailInfo | PhfOrderBaseInfo | IndexOrderInfo,
): order is PhfOrderBaseInfo => {
    return !('orderInfo' in order) && !isIndexOrderVo(order);
};

type HttpSuccessMessage<T extends number = number> = { code: T };

type NormalMessage = HttpSuccessMessage<0> & {
    /**
     * 轮询到的新订单详情
     *
     * Note: `commonInfo` 和 `orderInfo` 已被解析
     */
    data: NewOrderDetailMessage;
};
type NonZeroCodeMessage = HttpSuccessMessage & { data: null };

type HttpFailMessage = null;

const isNormalMessage = (
    message: NormalMessage | NonZeroCodeMessage | HttpFailMessage,
): message is NormalMessage => {
    return message?.code === 0;
};

/**
 * 获取新订单详情列表
 *
 * 注意：
 * 1. 原始响应中，`newOrders` 中每一项的 `orderInfo` 和 `commonInfo` 字段是 JSON 字符串，此处会解析
 * 2. `newOrders` 可能同时有普通和拼好饭订单，两者数据结构不一样
 * 3. `newIndexOrders` 包含处理新订单需要的最简信息，优先使用；`newIndexOrders` 为空时再使用 `newOrders`，后续 `newOrders` 将移除
 */
const getNewOrderListOld = (params: {
    /** 自动接单标识 0-非自动 1-自动 */
    isAutoAccept: 0 | 1;
    /** 订单数据时间戳 */
    dateAndSeq: string;
    /** 订单数量时间戳 */
    countDateAndSeq: string;
    /** 是否只获取count，false会返回新订单详情 */
    isCountReq: boolean;
}): Promise<NormalMessage | NonZeroCodeMessage> => {
    return request
        .rawPostAsFormSubmit<RawPostNewOrderListResponse>(
            '/gw/api/order/mix/new/interval/list/common',
            params,
        )
        .then(res => {
            if (res.code === 0) {
                return {
                    code: 0,
                    data: {
                        ...res.data,
                        newOrders: (res.data.newOrders || []).map(
                            ({ orderInfo, commonInfo, businessType }) => {
                                return {
                                    ...JSON.parse(orderInfo),
                                    ...JSON.parse(commonInfo),
                                    businessType,
                                };
                            },
                        ),
                    },
                };
            } else {
                return {
                    code: res.code,
                    data: null,
                };
            }
        });
};

const getNewOrderList = (params: {
    /** 自动接单标识 0-非自动 1-自动 */
    isAutoAccept: 0 | 1;
    /** 订单数据时间戳 */
    dateAndSeq: string;
    /** 订单数量时间戳 */
    countDateAndSeq: string;
    /** 是否只获取count，false会返回新订单详情 */
    isCountReq: boolean;
    scIndex: 0;
}): Promise<NormalMessage | NonZeroCodeMessage> => {
    return request
        .rawPostAsFormSubmit<RawPostNewOrderListResponse>(
            '/gw/api/unified/r/order/list/interval',
            params,
        )
        .then(res => {
            if (res.code === 0) {
                return {
                    code: 0,
                    data: {
                        ...res.data,
                        newOrders: (res.data.newOrders || []).map(
                            ({ orderInfo, commonInfo, businessType }) => {
                                return {
                                    ...JSON.parse(orderInfo),
                                    ...JSON.parse(commonInfo),
                                    businessType,
                                };
                            },
                        ),
                    },
                };
            } else {
                return {
                    code: res.code,
                    data: null,
                };
            }
        });
};

// eslint-disable-next-line waimaie/no-direct-access-localstorage
const isCountReq = !localStorage.getItem('autoAcceptWmPoiList');

const wmPoiId = Ids.getWmPoiId();
const labelInfoStorageKey = `${wmPoiId}-labelInfo`;
const countLabelInfoStorageKey = `${wmPoiId}-countLabelInfo`;

const customSourceBuilder = () => {
    let currentLoopInterval = 60;
    const pollingIntervalInSeconds$ = new BehaviorSubject(currentLoopInterval);
    const source$ = pollingIntervalInSeconds$.pipe(
        switchMap(pollingInterval => interval(pollingInterval * 1000)),
        startWith(0),
        switchMap(() =>
            from(
                getGrayStateByToken(GRAY_AB_TOKEN.UNIFIED_ORDER)
                    ? // 履约新形态轮询借口
                      getNewOrderList({
                          isAutoAccept:
                              getCookie('isChain') === '1' && ifPoiEnableAutoAcceptLocally(wmPoiId)
                                  ? 1
                                  : 0,
                          dateAndSeq: getStorage(labelInfoStorageKey) || '',
                          countDateAndSeq: getStorage(countLabelInfoStorageKey) || '',
                          isCountReq,
                          scIndex: 0,
                      })
                    : getNewOrderListOld({
                          isAutoAccept:
                              getCookie('isChain') === '1' && ifPoiEnableAutoAcceptLocally(wmPoiId)
                                  ? 1
                                  : 0,
                          dateAndSeq: getStorage(labelInfoStorageKey) || '',
                          countDateAndSeq: getStorage(countLabelInfoStorageKey) || '',
                          isCountReq,
                      }),
            ).pipe(catchError(() => of(null))),
        ),
        // 自定义指标上报
        tap(res => {
            if (res) {
                reportCustomMetrics(CUSTOM_METRICS.ORDER_OFQ_API.KEY, 1, {
                    [CUSTOM_METRICS.ORDER_OFQ_API.TAGS.HTTP_SUCCESS]: 1,
                    [CUSTOM_METRICS.ORDER_OFQ_API.TAGS.BUSINESS_CODE]: res.code,
                    [CUSTOM_METRICS.ORDER_OFQ_API.TAGS.IS_UNIFIED]: UNIFIED_ORDER_GRAY ? 1 : 0,
                    [CUSTOM_METRICS.ORDER_OFQ_API.TAGS.INITIATOR]: 'message_hub',
                });
            } else {
                reportCustomMetrics(CUSTOM_METRICS.ORDER_OFQ_API.KEY, 1, {
                    [CUSTOM_METRICS.ORDER_OFQ_API.TAGS.HTTP_SUCCESS]: 0,
                    [CUSTOM_METRICS.ORDER_OFQ_API.TAGS.IS_UNIFIED]: UNIFIED_ORDER_GRAY ? 1 : 0,
                    [CUSTOM_METRICS.ORDER_OFQ_API.TAGS.INITIATOR]: 'message_hub',
                });
            }
        }),
        share(),
    );

    networkMonitor.takeCare(
        source$.pipe(
            map(res => {
                if (!res) return { network: 'error', logon: 'unknown' };
                if (res.code === 1001) return { network: 'success', logon: 'error' };
                return { network: 'success', logon: 'success' };
            }),
        ),
    );

    return source$.pipe(
        filter(isNormalMessage),
        map(res => res.data),

        // 降级，命中灰度同时降级时，刷新页面重新获取灰度标识
        tap(({ signalGray }) => {
            if (
                (getGrayStateByToken(GRAY_AB_TOKEN.ZIPEI_MEAL_REPORT) &&
                    getDowngradeByBit(signalGray, GRAY_AB_TOKEN.ZIPEI_MEAL_REPORT)) ||
                (getGrayStateByToken(GRAY_AB_TOKEN.FOOD_SAFETY_REFUND) &&
                    getDowngradeByBit(signalGray, GRAY_AB_TOKEN.FOOD_SAFETY_REFUND)) ||
                (getGrayStateByToken(GRAY_AB_TOKEN.FAST_REFUND) &&
                    getDowngradeByBit(signalGray, GRAY_AB_TOKEN.FAST_REFUND)) ||
                (getGrayStateByToken(GRAY_AB_TOKEN.PREMEAL_REPORT) &&
                    getDowngradeByBit(signalGray, GRAY_AB_TOKEN.PREMEAL_REPORT)) ||
                (getGrayStateByToken(GRAY_AB_TOKEN.UNIFIED_ORDER) &&
                    getDowngradeByBit(signalGray, GRAY_AB_TOKEN.UNIFIED_ORDER)) ||
                (getGrayStateByToken(GRAY_AB_TOKEN.PHF_ORDER_CARD) &&
                    getDowngradeByBit(signalGray, GRAY_AB_TOKEN.PHF_ORDER_CARD))
            ) {
                // eslint-disable-next-line eqeqeq
                if (!Ids.getscIndex() || Ids.getscIndex() == '0') {
                    window.location.reload();
                }
            }
        }),

        // 更新轮询参数（接口入参和轮询间隔）
        tap(res => {
            const oldCountDateAndSeq = getStorage(countLabelInfoStorageKey) || '';
            if (res.countDateAndSeq && res.countDateAndSeq !== oldCountDateAndSeq) {
                setStorage(countLabelInfoStorageKey, res.countDateAndSeq);
            }

            const oldDateAndSeq = getStorage(labelInfoStorageKey) || '';
            if (res.dateAndSeq && res.dateAndSeq !== oldDateAndSeq) {
                setStorage(labelInfoStorageKey, res.dateAndSeq);
            }

            if (res.intervalSec && res.intervalSec !== currentLoopInterval) {
                currentLoopInterval = res.intervalSec;
                pollingIntervalInSeconds$.next(currentLoopInterval);
            }
        }),
        tap(setLastNormalMessage),
        share(),
    );
};

/** 初始化新订单轮询消息源 */
export default (instance: RootMasterMessageHub<any, CustomSourceKey>) => {
    instance.useCustomSource('newOrderDetailPoller', customSourceBuilder);
};
export { OrderBusinessType };
